import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Box, Stack, Switch, Typography } from '@mui/material';

type SeatRowAccent = 'success' | 'info' | 'primary';

type Props = {
  accent: SeatRowAccent;
  count: number;
  disabled?: boolean;
  filterButton: React.ReactNode;
  icon: React.ReactNode;
  label: string;
  onToggle?: (enabled: boolean) => void;
  showToggle?: boolean;
  testId?: string;
  toggleEnabled?: boolean;
};

/**
 * Single role row with optional enable toggle and a right-aligned filter control.
 */
const ModuleFilterRow = ({
  accent,
  count,
  disabled = false,
  filterButton,
  icon,
  label,
  onToggle,
  showToggle = false,
  testId,
  toggleEnabled = true,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const rowDisabled = disabled || (showToggle && !toggleEnabled);

  return (
    <Stack
      data-testid={testId}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '6px',
        backgroundColor: rowDisabled ? theme.palette.action.hover : theme.palette.background.paper,
        opacity: rowDisabled ? 0.72 : 1,
        padding: theme.spacing(1, 1.25),
      })}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        {showToggle && onToggle && (
          <Switch
            size="small"
            checked={toggleEnabled}
            onChange={(_, checked) => onToggle(checked)}
            inputProps={{ 'aria-label': label }}
            data-testid={`${testId}-toggle`}
          />
        )}
        <Box
          sx={(theme) => ({
            display: 'inline-flex',
            color: theme.palette[accent].main,
            '& svg': { fontSize: 16 },
          })}
        >
          {icon}
        </Box>
        <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.3} sx={{ minWidth: 0 }}>
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" lineHeight={1.2} sx={{ whiteSpace: 'nowrap' }}>
          {t('hiring-dashboard-filter:filter.matchingMembersCount', { count })}
        </Typography>
        <Box sx={{ position: 'relative', pointerEvents: rowDisabled ? 'none' : 'auto' }}>{filterButton}</Box>
      </Stack>
    </Stack>
  );
};

export default ModuleFilterRow;
